import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Button, Modal, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getMaterials, createMaterial, updateMaterial, deleteMaterial, getDuplicateMaterials, removeExactDuplicates, removeAllDuplicates, enrichMaterials } from '../../../services/MaterialLogsService';
import { formatError } from '../../../services/AuthService';
import { quantityToGrams, formatUnitForDisplay, isConvertible, convert } from '../../../utils/unitConversion';
import { checkCaloriesMatch } from '../../../utils/nutritionUtils';
import { getMaterialName } from '../../../utils/materialName';
import { EU_ALLERGENS, parseAllergenList } from '../Recipes/recipePrintableLabelUtils';
import UnitSelector from '../Common/UnitSelector';

// Separate component for quantity display to ensure re-rendering
const MaterialQuantityDisplay = React.memo(({ material, displayUnit, onUnitChange, forceUpdate }) => {
    const materialUnit = (material.unit || 'unit').trim();
    const quantity = parseFloat(material.quantity_purchased ?? 1);
    
    // Convert quantity from material's stored unit to display unit
    const materialUnitNorm = materialUnit.toLowerCase();
    const displayUnitNorm = displayUnit.toLowerCase();
    
    // Always convert if units are different and both are convertible
    let displayedQuantity = quantity;
    if (isConvertible(materialUnit) && isConvertible(displayUnit)) {
        if (materialUnitNorm !== displayUnitNorm) {
            displayedQuantity = convert(quantity, materialUnit, displayUnit);
        }
    }
    
    // Format the displayed quantity
    let formattedQty = String(displayedQuantity);
    if (displayedQuantity >= 1) {
        formattedQty = displayedQuantity % 1 === 0 
            ? String(Math.round(displayedQuantity))
            : displayedQuantity.toFixed(2).replace(/\.?0+$/, '');
    } else if (displayedQuantity > 0) {
        formattedQty = displayedQuantity.toFixed(4).replace(/\.?0+$/, '');
    }
    
    return (
        <div className="d-flex align-items-center justify-content-center gap-2">
            <span>{formattedQty}</span>
            {isConvertible(materialUnit) ? (
                <UnitSelector
                    unit={displayUnit}
                    onUnitChange={onUnitChange}
                    quantity={displayedQuantity}
                    onQuantityChange={() => {}}
                    materialUnit={materialUnit}
                />
            ) : (
                <span>{formatUnitForDisplay(materialUnit)}</span>
            )}
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison - re-render if displayUnit or forceUpdate changes
    return prevProps.displayUnit === nextProps.displayUnit && 
           prevProps.forceUpdate === nextProps.forceUpdate &&
           prevProps.material.id === nextProps.material.id &&
           prevProps.material.quantity_purchased === nextProps.material.quantity_purchased &&
           prevProps.material.unit === nextProps.material.unit;
});

const emptyForm = {
    material_type: 'atomic',
    material_name: '',
    name_nl: '',
    price: '',
    quantity_purchased: '1',
    unit: 'unit',
    price_includes_tax: true,
    tax_percentage: '',
    is_non_food: false,
    energy_for_purchase: '',
    protein_for_purchase: '',
    fat_for_purchase: '',
    saturated_fat_for_purchase: '',
    trans_fat_for_purchase: '',
    carbohydrate_for_purchase: '',
    sugars_for_purchase: '',
    fiber_for_purchase: '',
    sodium_for_purchase: '',
    allergens: [],
    may_contain: '',
    source: '',
    location: '',
    notes: '',
    // Compound only: [{ component_material_id, quantity }]
    components: [],
};

const emptyComponentRow = { component_material_id: '', quantity: '1' };

// checkCaloriesMatch is imported from nutritionUtils (single source of truth)

const MaterialLogs = () => {
    const { t } = useTranslation();
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [duplicates, setDuplicates] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewFilter, setViewFilter] = useState('all');
    // Track display units for each material (for unit conversion in table)
    const [displayUnits, setDisplayUnits] = useState({});
    const [forceUpdate, setForceUpdate] = useState(0); // Force re-render when units change
    const [expandedIds, setExpandedIds] = useState(() => new Set()); // compound rows showing their components

    const filteredMaterials = useMemo(() => {
        let list = materials;
        if (viewFilter === 'recipes') list = list.filter((m) => m.used_in_recipes);
        else if (viewFilter === 'rd') list = list.filter((m) => m.used_in_rd);
        else if (viewFilter === 'notused') list = list.filter((m) => !m.used_in_recipes && !m.used_in_rd);
        const q = (searchQuery || '').trim().toLowerCase();
        if (!q) return list;
        return list.filter((m) => {
            const name = (m.material_name || '').toLowerCase();
            const nameNl = (m.name_nl || '').toLowerCase();
            const source = (m.source || '').toLowerCase();
            const location = (m.location || '').toLowerCase();
            const notes = (m.notes || '').toLowerCase();
            const unit = (m.unit || '').toLowerCase();
            const priceStr = String(m.price || '');
            return name.includes(q) || nameNl.includes(q) || source.includes(q) || location.includes(q) ||
                notes.includes(q) || unit.includes(q) || priceStr.includes(q);
        });
    }, [materials, searchQuery, viewFilter]);

    const hasExactDuplicates = useMemo(() => {
        const byKey = {};
        for (const m of materials) {
            const name = (m.material_name || '').trim().toLowerCase();
            const price = parseFloat(m.price) || 0;
            const qty = parseFloat(m.quantity_purchased) || 1;
            const unit = (m.unit || 'unit').trim().toLowerCase();
            const incTax = !!m.price_includes_tax;
            const tax = m.tax_percentage != null ? parseFloat(m.tax_percentage) : null;
            const key = `${name}|${price}|${qty}|${unit}|${incTax}|${tax}`;
            byKey[key] = (byKey[key] || 0) + 1;
        }
        return Object.values(byKey).some((count) => count > 1);
    }, [materials]);

    const NUTRITION_WARNING_EXCEPTIONS = ['okra'];

    const materialsWithWrongNutrition = useMemo(() => {
        return materials.filter((m) => {
            if (m.is_non_food) return false;
            if (NUTRITION_WARNING_EXCEPTIONS.includes((m.material_name || '').toLowerCase().trim())) return false;
            const e = m.energy_per_100g;
            const p = m.protein_per_100g;
            const f = m.fat_per_100g;
            const c = m.carbohydrate_per_100g;
            const fib = m.fiber_per_100g;
            if (e == null || p == null || f == null || c == null) return false;
            return !checkCaloriesMatch(e, p, f, c, fib).ok;
        });
    }, [materials]);

    const loadMaterials = async () => {
        try {
            setLoading(true);
            const res = await getMaterials();
            setMaterials(res.data || []);
        } catch (err) {
            toast.error(err.response?.data?.error || formatError(err) || t('materials.errors.load'));
            setMaterials([]);
        } finally {
            setLoading(false);
        }
    };

    const loadDuplicates = async () => {
        try {
            const res = await getDuplicateMaterials();
            setDuplicates(res.data || []);
        } catch {
            setDuplicates([]);
        }
    };

    useEffect(() => {
        loadMaterials();
    }, []);

    useEffect(() => {
        if (!loading && materials.length > 0) {
            loadDuplicates();
        }
    }, [loading, materials.length]);

    // Per-unit excl/incl price for any material (atomic or compound) — compound materials
    // already carry a live-computed price_excl_tax/price_incl_tax from the API, so this
    // works the same way for both.
    const getUnitPrices = useCallback((material) => {
        if (!material) return { excl: 0, incl: 0 };
        const qty = parseFloat(material.quantity_purchased) || 1;
        const excl = material.price_excl_tax != null ? parseFloat(material.price_excl_tax) / qty : 0;
        const incl = material.price_incl_tax != null ? parseFloat(material.price_incl_tax) / qty : (material.price_per_unit || 0);
        return { excl, incl };
    }, []);

    const componentsTotal = useMemo(() => {
        let excl = 0;
        let incl = 0;
        let anyFood = false;
        let anyNonFood = false;
        for (const row of formData.components || []) {
            const mat = materials.find((m) => String(m.id) === String(row.component_material_id));
            const qty = parseFloat(row.quantity) || 0;
            if (!mat || qty <= 0) continue;
            const { excl: exclUnit, incl: inclUnit } = getUnitPrices(mat);
            excl += exclUnit * qty;
            incl += inclUnit * qty;
            if (mat.is_non_food && !mat.is_food_mixed) anyNonFood = true;
            else anyFood = true;
        }
        return {
            excl,
            incl,
            isNonFood: anyNonFood && !anyFood,
            isFoodMixed: anyFood && anyNonFood,
        };
    }, [formData.components, materials, getUnitPrices]);

    const handleComponentRowChange = (idx, field, value) => {
        setFormData((prev) => {
            const next = [...(prev.components || [])];
            next[idx] = { ...next[idx], [field]: value };
            return { ...prev, components: next };
        });
    };

    const handleAddComponentRow = () => {
        setFormData((prev) => ({ ...prev, components: [...(prev.components || []), { ...emptyComponentRow }] }));
    };

    const handleRemoveComponentRow = (idx) => {
        setFormData((prev) => ({ ...prev, components: (prev.components || []).filter((_, i) => i !== idx) }));
    };

    const handleMaterialTypeChange = (type) => {
        setFormData((prev) => ({
            ...prev,
            material_type: type,
            components: type === 'compound' ? (prev.components.length ? prev.components : [{ ...emptyComponentRow }]) : [],
        }));
    };

    const toggleExpanded = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
            if (name === 'is_non_food' && checked) {
                next.energy_for_purchase = '';
                next.protein_for_purchase = '';
                next.fat_for_purchase = '';
                next.saturated_fat_for_purchase = '';
                next.trans_fat_for_purchase = '';
                next.carbohydrate_for_purchase = '';
                next.sugars_for_purchase = '';
                next.fiber_for_purchase = '';
                next.sodium_for_purchase = '';
                next.allergens = [];
                next.may_contain = '';
            }
            return next;
        });
    };

    const handleAllergenToggle = (allergen) => {
        setFormData((prev) => {
            const selected = new Set(prev.allergens || []);
            if (selected.has(allergen)) {
                selected.delete(allergen);
            } else {
                selected.add(allergen);
            }
            return {
                ...prev,
                allergens: EU_ALLERGENS.filter((item) => selected.has(item)),
            };
        });
    };

    const resetForm = () => {
        setFormData(emptyForm);
        setEditingId(null);
    };

    const handleClose = () => {
        setShowModal(false);
        resetForm();
    };

    const handleEdit = (m) => {
        if (m.material_type === 'compound') {
            setFormData({
                ...emptyForm,
                material_type: 'compound',
                material_name: m.material_name,
                name_nl: m.name_nl || '',
                unit: m.unit || 'unit',
                is_non_food: m.is_non_food || false,
                source: m.source || '',
                location: m.location || '',
                notes: m.notes || '',
                components: (m.components || []).map((c) => ({
                    component_material_id: String(c.component_material_id),
                    quantity: String(c.quantity),
                })),
            });
            setEditingId(m.id);
            setShowModal(true);
            return;
        }
        const qty = parseFloat(m.quantity_purchased) || 1;
        const unit = m.unit || 'unit';
        const grams = quantityToGrams(qty || 1, unit);
        const factor = grams / 100;
        setFormData({
            material_type: 'atomic',
            components: [],
            material_name: m.material_name,
            name_nl: m.name_nl || '',
            price: String(m.price),
            quantity_purchased: String(m.quantity_purchased ?? 1),
            unit: m.unit || 'unit',
            price_includes_tax: m.price_includes_tax,
            tax_percentage: m.tax_percentage != null ? String(m.tax_percentage) : '',
            is_non_food: m.is_non_food || false,
            energy_for_purchase:         m.energy_per_100g        != null ? String(Math.round(m.energy_per_100g        * factor * 100) / 100) : '',
            protein_for_purchase:        m.protein_per_100g       != null ? String(Math.round(m.protein_per_100g       * factor * 100) / 100) : '',
            fat_for_purchase:            m.fat_per_100g           != null ? String(Math.round(m.fat_per_100g           * factor * 100) / 100) : '',
            saturated_fat_for_purchase:  m.saturated_fat_per_100g != null ? String(Math.round(m.saturated_fat_per_100g * factor * 100) / 100) : '',
            trans_fat_for_purchase:      m.trans_fat_per_100g     != null ? String(Math.round(m.trans_fat_per_100g     * factor * 100) / 100) : '',
            carbohydrate_for_purchase:   m.carbohydrate_per_100g  != null ? String(Math.round(m.carbohydrate_per_100g  * factor * 100) / 100) : '',
            sugars_for_purchase:         m.sugars_per_100g        != null ? String(Math.round(m.sugars_per_100g        * factor * 100) / 100) : '',
            fiber_for_purchase:          m.fiber_per_100g         != null ? String(Math.round(m.fiber_per_100g         * factor * 100) / 100) : '',
            sodium_for_purchase:         m.sodium_per_100g        != null ? String(Math.round(m.sodium_per_100g        * factor * 10000) / 10000) : '',
            allergens: parseAllergenList(m.allergens),
            may_contain: m.may_contain || '',
            source: m.source || '',
            location: m.location || '',
            notes: m.notes || '',
        });
        setEditingId(m.id);
        setShowModal(true);
    };

    const handleAdd = () => {
        resetForm();
        setShowModal(true);
    };

    const handleRemoveExactDuplicates = async () => {
        const result = await Swal.fire({
            title: t('materials.removeExactDuplicatesTitle'),
            text: t('materials.removeExactDuplicatesText'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#0d6efd',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('materials.removeDuplicates'),
            cancelButtonText: t('common.cancel'),
        });
        if (!result.isConfirmed) return;
        try {
            setSubmitting(true);
            const res = await removeExactDuplicates();
            const removed = res.data?.removed ?? 0;
            if (removed > 0) {
                toast.success(res.data?.message || `Removed ${removed} duplicate(s)`);
                await loadMaterials();
                loadDuplicates();
            } else {
                toast.info(t('materials.info.noExactDuplicates'));
            }
        } catch (err) {
            toast.error(err.response?.data?.error || t('materials.errors.removeDuplicates'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveAllDuplicates = async () => {
        const result = await Swal.fire({
            title: t('materials.removeAllDuplicatesTitle'),
            text: t('materials.removeAllDuplicatesText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('materials.removeDuplicates'),
            cancelButtonText: t('common.cancel'),
        });
        if (!result.isConfirmed) return;
        try {
            setSubmitting(true);
            const res = await removeAllDuplicates();
            const removed = res.data?.removed ?? 0;
            if (removed > 0) {
                toast.success(res.data?.message || `Removed ${removed} duplicate(s)`);
                await loadMaterials();
                loadDuplicates();
            } else {
                toast.info(t('materials.info.noDuplicates'));
            }
        } catch (err) {
            toast.error(err.response?.data?.error || t('materials.errors.removeDuplicates'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEnrichMaterials = async () => {
        const result = await Swal.fire({
            title: t('materials.autoCompleteTitle'),
            text: t('materials.autoCompleteText'),
            icon: 'info',
            showCancelButton: true,
            confirmButtonColor: '#0d6efd',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('materials.fillMissingFields'),
            cancelButtonText: t('common.cancel'),
        });
        if (!result.isConfirmed) return;
        try {
            setSubmitting(true);
            const res = await enrichMaterials();
            const updated = res.data?.updated ?? 0;
            if (updated > 0) {
                toast.success(t('materials.success.filledNutrition', { count: updated }));
                await loadMaterials();
            } else {
                toast.info(t('materials.info.noEnrichmentNeeded'));
            }
        } catch (err) {
            toast.error(err.response?.data?.error || t('materials.errors.enrich'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.material_name.trim()) {
            toast.warning(t('materials.errors.materialNameRequired'));
            return;
        }
        if (!formData.name_nl.trim()) {
            toast.warning(editingId ? t('materials.dutchNameRequired') : t('materials.nameDutchWarning'));
            return;
        }

        if (formData.material_type === 'compound') {
            const rows = (formData.components || []).filter((r) => r.component_material_id);
            if (rows.length === 0) {
                toast.warning(t('materials.compound.noComponentsYet'));
                return;
            }
            for (const row of rows) {
                if (!row.quantity || parseFloat(row.quantity) <= 0) {
                    toast.warning(t('materials.compound.componentQuantity'));
                    return;
                }
            }
            const payload = {
                material_type: 'compound',
                material_name: formData.material_name.trim(),
                name_nl: formData.name_nl.trim(),
                unit: formData.unit.trim() || 'unit',
                source: formData.source.trim() || null,
                location: formData.location.trim() || null,
                notes: formData.notes.trim() || null,
                components: rows.map((r) => ({
                    component_material_id: parseInt(r.component_material_id, 10),
                    quantity: parseFloat(r.quantity),
                })),
            };
            try {
                setSubmitting(true);
                if (editingId) {
                    await updateMaterial(editingId, payload);
                    toast.success(t('materials.success.updated'));
                } else {
                    await createMaterial(payload);
                    toast.success(t('materials.success.added'));
                }
                handleClose();
                loadMaterials();
                loadDuplicates();
            } catch (err) {
                const msg = err.response?.data?.error || err.message || t('materials.errors.save');
                toast.error(msg);
            } finally {
                setSubmitting(false);
            }
            return;
        }

        if (!formData.price || parseFloat(formData.price) < 0) {
            toast.warning(t('materials.errors.validPriceRequired'));
            return;
        }

        const payload = {
            material_type: 'atomic',
            material_name: formData.material_name.trim(),
            name_nl: formData.name_nl.trim(),
            price: parseFloat(formData.price),
            quantity_purchased: parseFloat(formData.quantity_purchased) || 1,
            unit: formData.unit.trim() || 'unit',
            price_includes_tax: formData.price_includes_tax,
            tax_percentage: formData.tax_percentage ? parseFloat(formData.tax_percentage) : null,
            is_non_food: formData.is_non_food,
            allergens: formData.is_non_food ? '' : (formData.allergens || []).join(', '),
            may_contain: formData.is_non_food ? '' : (formData.may_contain.trim() || ''),
            source: formData.source.trim() || null,
            location: formData.location.trim() || null,
            notes: formData.notes.trim() || null,
        };
        if (!formData.is_non_food) {
            const energy = parseFloat(formData.energy_for_purchase);
            const protein = parseFloat(formData.protein_for_purchase);
            const fat = parseFloat(formData.fat_for_purchase);
            const carb = parseFloat(formData.carbohydrate_for_purchase);
            const fiber = parseFloat(formData.fiber_for_purchase);
            if ([energy, protein, fat, carb, fiber].some(v => isNaN(v) || v < 0)) {
                toast.warning(t('materials.errors.requiredNutrition'));
                return;
            }
            const calCheck = checkCaloriesMatch(energy, protein, fat, carb, fiber);
            if (!calCheck.ok) {
                const result = await Swal.fire({
                    title: t('materials.caloriesMismatchTitle'),
                    html: t('materials.caloriesMismatchText', {
                        entered: calCheck.entered,
                        expected: calCheck.expected.toFixed(1),
                    }),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#0d6efd',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: t('materials.saveAnyway'),
                    cancelButtonText: t('common.cancel'),
                });
                if (!result.isConfirmed) return;
            }
            const grams = quantityToGrams(formData.quantity_purchased || 1, formData.unit);
            const factor = grams / 100;
            if (factor <= 0) {
                toast.warning(t('materials.errors.quantityRequired'));
                return;
            }
            const per100 = (v) => Math.round((v / factor) * 100) / 100;
            payload.energy_per_100g        = per100(energy);
            payload.protein_per_100g       = per100(protein);
            payload.fat_per_100g           = per100(fat);
            payload.carbohydrate_per_100g  = per100(carb);
            payload.fiber_per_100g         = per100(fiber);
            // Optional sub-fields — only include when provided
            const satFat  = parseFloat(formData.saturated_fat_for_purchase);
            const transFat = parseFloat(formData.trans_fat_for_purchase);
            const sugars  = parseFloat(formData.sugars_for_purchase);
            const sodium  = parseFloat(formData.sodium_for_purchase);
            if (!isNaN(satFat)   && satFat  >= 0) payload.saturated_fat_per_100g = per100(satFat);
            if (!isNaN(transFat) && transFat >= 0) payload.trans_fat_per_100g    = per100(transFat);
            if (!isNaN(sugars)   && sugars  >= 0) payload.sugars_per_100g       = per100(sugars);
            if (!isNaN(sodium)   && sodium  >= 0) payload.sodium_per_100g       = Math.round((sodium / factor) * 10000) / 10000;
        }

        try {
            setSubmitting(true);
            if (editingId) {
                await updateMaterial(editingId, payload);
                toast.success(t('materials.success.updated'));
            } else {
                await createMaterial(payload);
                toast.success(t('materials.success.added'));
            }
            handleClose();
            loadMaterials();
            loadDuplicates();
        } catch (err) {
            const msg = err.response?.data?.error || err.message || t('materials.errors.save');
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            title: t('materials.deleteTitle'),
            text: t('materials.deleteText', { name }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('materials.yesDelete'),
            cancelButtonText: t('common.cancel'),
        });

        if (result.isConfirmed) {
            try {
                await deleteMaterial(id);
                toast.success(t('materials.success.deleted'));
                loadMaterials();
            } catch (err) {
                toast.error(err.response?.data?.error || t('materials.errors.delete'));
            }
        }
    };

    const formatPrice = (val) => {
        if (val == null) return '-';
        return parseFloat(val).toFixed(2);
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header border-0 d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div>
                                <h4 className="heading mb-0">{t('materials.title')}</h4>
                            </div>
                            <div className="d-flex align-items-center gap-2 mobile-toolbar flex-wrap">
                                <div className="d-flex gap-1">
                                    {[
                                        { key: 'all', label: t('materials.filterAll') },
                                        { key: 'recipes', label: t('materials.filterRecipes') },
                                        { key: 'rd', label: t('materials.filterRd') },
                                        { key: 'notused', label: t('materials.filterNotUsed') },
                                    ].map(({ key, label }) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setViewFilter(key)}
                                            style={{
                                                padding: '4px 14px',
                                                borderRadius: 6,
                                                fontSize: 13,
                                                fontWeight: viewFilter === key ? 600 : 400,
                                                border: viewFilter === key ? '1.5px solid #452B90' : '1.5px solid #ced4da',
                                                background: viewFilter === key ? '#452B90' : 'transparent',
                                                color: viewFilter === key ? '#fff' : '#6c757d',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div className="input-group mobile-toolbar-search" style={{ minWidth: 280, width: 360 }}>
                                    <span className="input-group-text bg-light">
                                        <i className="fa-solid fa-magnifying-glass text-muted" />
                                    </span>
                                    <Form.Control
                                        type="search"
                                        placeholder={t('materials.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        aria-label={t('materials.searchAria')}
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setSearchQuery('')}
                                            title={t('common.clearSearch')}
                                            aria-label={t('common.clearSearch')}
                                        >
                                            <i className="fa-solid fa-xmark text-muted" />
                                        </button>
                                    )}
                                </div>
                                {hasExactDuplicates && (
                                    <Button variant="outline-secondary" onClick={handleRemoveExactDuplicates} disabled={submitting} className="me-2 mobile-toolbar-add-btn">
                                        <i className="fa-solid fa-broom me-1" /> {t('materials.removeExactDuplicates')}
                                    </Button>
                                )}
                                <Button variant="outline-success" onClick={handleEnrichMaterials} disabled={submitting} className="me-2 mobile-toolbar-add-btn" title={t('materials.autoCompleteText')}>
                                    <i className="fa-solid fa-wand-magic-sparkles me-1" /> {t('materials.autoFillNutrition')}
                                </Button>
                                <Button variant="primary" onClick={handleAdd} className="mobile-toolbar-add-btn">
                                    <i className="fa-solid fa-plus me-1" /> {t('materials.addMaterial')}
                                </Button>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            {duplicates.length > 0 && (
                                <div className="alert alert-warning m-3 mb-0 d-flex align-items-start flex-wrap" role="alert">
                                    <i className="fa-solid fa-triangle-exclamation me-2 mt-1" />
                                    <div className="flex-grow-1">
                                        <strong>{t('materials.duplicateMaterialsDetected')}</strong> - same name, different prices: {duplicates.map((d) => d.name).join(', ')}
                                        <div className="mt-2 small">
                                            {duplicates.map((d) => (
                                                <div key={d.name}>
                                                    <strong>{d.name}:</strong>{' '}
                                                    {d.items.map((i) => `€${i.price_incl_tax?.toFixed(2)} (id ${i.id})`).join(', ')}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <Button variant="warning" size="sm" className="ms-2" onClick={handleRemoveAllDuplicates} disabled={submitting}>
                                        <i className="fa-solid fa-broom me-1" /> {t('materials.removeAllDuplicates')}
                                    </Button>
                                </div>
                            )}
                            {materialsWithWrongNutrition.length > 0 && (
                                <div className="alert alert-warning m-3 mb-0" role="alert">
                                    <i className="fa-solid fa-triangle-exclamation me-2" />
                                    <strong>{t('materials.checkNutritionWarning')}</strong>{' '}
                                    {materialsWithWrongNutrition.map((m) => m.material_name).join(', ')}
                                    <p className="mb-0 mt-1 small">{t('materials.checkNutritionHint')}</p>
                                </div>
                            )}
                            {searchQuery && materials.length > 0 && (
                                <div className="px-3 pt-2">
                                    <small className="text-muted">
                                        {t('common.showingResults', {
                                            shown: filteredMaterials.length,
                                            total: materials.length,
                                            label: t('materials.title').toLowerCase(),
                                        })}
                                        <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => setSearchQuery('')}>
                                            {t('common.clearSearch')}
                                        </button>
                                    </small>
                                </div>
                            )}
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status" />
                                    <p className="mt-2 text-muted mb-0">{t('materials.loading')}</p>
                                </div>
                            ) : (
                                <div className="table-responsive materials-logs-table-wrap">
                                    <table className="table table-hover mb-0 align-middle materials-logs-table">
                                        <colgroup>
                                            <col style={{ width: '18%' }} />
                                            <col style={{ width: '8%' }} />
                                            <col style={{ width: '13%' }} />
                                            <col style={{ width: '8%' }} />
                                            <col style={{ width: '10%' }} />
                                            <col style={{ width: '9%' }} />
                                            <col style={{ width: '9%' }} />
                                            <col style={{ width: '10%' }} />
                                            <col style={{ width: '9%' }} />
                                            <col style={{ width: '6%' }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>{t('materials.columns.material')}</th>
                                                <th className="text-center">{t('materials.columns.priceEntered')}</th>
                                                <th className="text-center">{t('materials.columns.qtyUnit')}</th>
                                                <th className="text-center">{t('materials.columns.pricePerUnit')}</th>
                                                <th className="text-center">{t('materials.columns.taxInfo')}</th>
                                                <th className="text-center">{t('materials.columns.priceExclTax')}</th>
                                                <th className="text-center">{t('materials.columns.priceInclTax')}</th>
                                                <th>{t('materials.columns.source')}</th>
                                                <th>{t('materials.columns.location')}</th>
                                                <th className="text-end">{t('materials.columns.actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {materials.length === 0 ? (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-5 text-muted">
                                                        <p className="mb-1">{t('materials.noMaterialsYet')}</p>
                                                        <small>{t('materials.trackFirstMaterial')}</small>
                                                    </td>
                                                </tr>
                                            ) : filteredMaterials.length === 0 ? (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-5 text-muted">
                                                        {searchQuery ? (
                                                            <>
                                                                <p className="mb-1">{t('materials.noMaterialsMatch', { query: searchQuery })}</p>
                                                                <small>{t('materials.tryDifferentKeywords')}</small>
                                                            </>
                                                        ) : (
                                                            <p className="mb-1">{t(`materials.filterEmpty.${viewFilter}`)}</p>
                                                        )}
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredMaterials.map((m) => {
                                                    // Get current display unit for this material
                                                    const materialUnit = (m.unit || 'unit').trim();
                                                    const displayUnit = displayUnits[m.id] ? displayUnits[m.id].trim() : materialUnit;
                                                    const isCompound = m.material_type === 'compound';
                                                    const isExpanded = expandedIds.has(m.id);

                                                    return (
                                                    <React.Fragment key={`row-${m.id}-${displayUnit}-${forceUpdate}`}>
                                                    <tr>
                                                        <td>
                                                            {isCompound && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link btn-sm p-0 me-1"
                                                                    onClick={() => toggleExpanded(m.id)}
                                                                    title={isExpanded ? t('materials.compound.hideComponents') : t('materials.compound.viewComponents')}
                                                                >
                                                                    <i className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'}`} />
                                                                </button>
                                                            )}
                                                            <strong
                                                                className="d-inline-block text-truncate align-bottom"
                                                                style={{ maxWidth: '100%' }}
                                                                title={getMaterialName(m)}
                                                            >
                                                                {getMaterialName(m)}
                                                            </strong>
                                                            {isCompound && (
                                                                <span className="badge bg-info text-dark ms-1">{t('materials.compound.badge')}</span>
                                                            )}
                                                            {m.is_non_food && (
                                                                <span className="badge bg-secondary ms-1">{t('materials.nonFood')}</span>
                                                            )}
                                                            {m.is_food_mixed && (
                                                                <span className="badge bg-warning text-dark ms-1" title={t('materials.compound.mixedFoodWarning')}>
                                                                    <i className="fa-solid fa-triangle-exclamation" />
                                                                </span>
                                                            )}
                                                            {m.notes && (
                                                                <small className="d-block text-truncate text-muted" title={m.notes}>{m.notes}</small>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            {formatPrice(m.price)}
                                                            {isCompound && (
                                                                <small className="d-block text-muted">{t('materials.compound.computedPrice')}</small>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            {isCompound ? (
                                                                <span>1 {formatUnitForDisplay(materialUnit)}</span>
                                                            ) : (
                                                            <MaterialQuantityDisplay
                                                                key={`qty-display-${m.id}-${displayUnit}-${forceUpdate}`}
                                                                material={m}
                                                                displayUnit={displayUnit}
                                                                onUnitChange={(newUnit) => {
                                                                    const currentNorm = (displayUnit || materialUnit || '').trim().toLowerCase();
                                                                    const newNorm = (newUnit || '').trim().toLowerCase();
                                                                    // Always update if different (case-insensitive)
                                                                    if (currentNorm !== newNorm) {
                                                                        // Update display unit
                                                                        setDisplayUnits(prev => ({
                                                                            ...prev,
                                                                            [m.id]: newUnit
                                                                        }));
                                                                        // Force re-render
                                                                        setForceUpdate(prev => prev + 1);
                                                                    }
                                                                }}
                                                                forceUpdate={forceUpdate}
                                                            />
                                                            )}
                                                        </td>
                                                        <td className="text-center">{formatPrice(m.price_per_unit)}</td>
                                                        <td className="text-center">
                                                            {isCompound ? (
                                                                <span className="text-muted small">{t('materials.compound.typeCompound')}</span>
                                                            ) : m.tax_percentage != null ? (
                                                                <span>
                                                                    {m.price_includes_tax ? 'Incl.' : 'Excl.'} {formatPrice(m.tax_percentage)}%
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">{t('materials.noTax')}</span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">{formatPrice(m.price_excl_tax)}</td>
                                                        <td className="text-center">{formatPrice(m.price_incl_tax)}</td>
                                                        <td>
                                                            <span className="d-block text-truncate" title={m.source || ''}>{m.source || '-'}</span>
                                                        </td>
                                                        <td>
                                                            <span className="d-block text-truncate" title={m.location || ''}>{m.location || '-'}</span>
                                                        </td>
                                                        <td className="text-end">
                                                            <Button
                                                                variant="light"
                                                                size="sm"
                                                                className="me-1"
                                                                onClick={() => handleEdit(m)}
                                                                title={t('common.edit')}
                                                            >
                                                                <i className="fa-solid fa-pen text-primary" />
                                                            </Button>
                                                            <Button
                                                                variant="light"
                                                                size="sm"
                                                                className="text-danger"
                                                                onClick={() => handleDelete(m.id, m.material_name)}
                                                                title={t('common.delete')}
                                                            >
                                                                <i className="fa-solid fa-trash" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                    {isCompound && isExpanded && (
                                                        <tr className="table-light">
                                                            <td colSpan="10">
                                                                <div className="ps-4 py-2">
                                                                    <strong className="d-block mb-2">{t('materials.compound.components')}</strong>
                                                                    <table className="table table-sm mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>{t('materials.columns.material')}</th>
                                                                                <th className="text-center">{t('common.quantity')}</th>
                                                                                <th className="text-center">{t('materials.columns.pricePerUnit')}</th>
                                                                                <th className="text-center">{t('materials.columns.priceExclTax')}</th>
                                                                                <th className="text-center">{t('materials.columns.priceInclTax')}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {(m.components || []).map((c) => (
                                                                                <tr key={c.id}>
                                                                                    <td>{getMaterialName({ material_name: c.component_material_name, name_nl: c.component_material_name_nl })}</td>
                                                                                    <td className="text-center">{c.quantity} {formatUnitForDisplay(c.unit)}</td>
                                                                                    <td className="text-center">{formatPrice(c.price_per_unit_incl_tax)}</td>
                                                                                    <td className="text-center">{formatPrice(c.line_cost_excl_tax)}</td>
                                                                                    <td className="text-center">{formatPrice(c.line_cost_incl_tax)}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    </React.Fragment>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showModal} onHide={handleClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingId ? t('materials.editMaterial') : t('materials.addMaterial')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {!editingId ? (
                            <div className="mb-3 d-flex gap-2">
                                <Button
                                    type="button"
                                    variant={formData.material_type === 'atomic' ? 'primary' : 'outline-secondary'}
                                    onClick={() => handleMaterialTypeChange('atomic')}
                                    className="flex-fill text-start"
                                >
                                    <strong>{t('materials.compound.typeAtomic')}</strong>
                                    <small className="d-block">{t('materials.compound.typeAtomicHint')}</small>
                                </Button>
                                <Button
                                    type="button"
                                    variant={formData.material_type === 'compound' ? 'primary' : 'outline-secondary'}
                                    onClick={() => handleMaterialTypeChange('compound')}
                                    className="flex-fill text-start"
                                >
                                    <strong>{t('materials.compound.typeCompound')}</strong>
                                    <small className="d-block">{t('materials.compound.typeCompoundHint')}</small>
                                </Button>
                            </div>
                        ) : formData.material_type === 'compound' ? (
                            <div className="mb-3">
                                <span className="badge bg-info text-dark">{t('materials.compound.typeCompound')}</span>
                            </div>
                        ) : null}
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('materials.materialNameEN')} *</Form.Label>
                                    <Form.Control
                                        name="material_name"
                                        value={formData.material_name}
                                        onChange={handleChange}
                                        placeholder={t('materials.materialName')}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('materials.materialNameNL')} *</Form.Label>
                                    <Form.Control
                                        name="name_nl"
                                        value={formData.name_nl}
                                        onChange={handleChange}
                                        placeholder={t('materials.materialNameNLPlaceholder')}
                                        required={Boolean(editingId)}
                                    />
                                </Form.Group>
                            </Col>
                            {formData.material_type === 'atomic' ? (
                            <>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('common.price')} *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleChange}
                                        placeholder="0.00"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('materials.qtyPurchased')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        name="quantity_purchased"
                                        value={formData.quantity_purchased}
                                        onChange={handleChange}
                                        placeholder="1"
                                    />
                                    <Form.Text className="text-muted">{t('materials.qtyAtThisPrice')}</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('common.unit')}</Form.Label>
                                    {isConvertible(formData.unit) ? (
                                        <div>
                                            <UnitSelector
                                                unit={formData.unit}
                                                onUnitChange={(newUnit) => {
                                                    const currentQty = parseFloat(formData.quantity_purchased) || 1;
                                                    const convertedQty = convert(currentQty, formData.unit, newUnit);
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        unit: newUnit,
                                                        quantity_purchased: convertedQty.toString()
                                                    }));
                                                }}
                                                quantity={parseFloat(formData.quantity_purchased) || 1}
                                                onQuantityChange={(newQty) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        quantity_purchased: newQty.toString()
                                                    }));
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <Form.Control
                                            name="unit"
                                            value={formData.unit}
                                            onChange={handleChange}
                                            placeholder={t('common.unit')}
                                        />
                                    )}
                                </Form.Group>
                            </Col>
                            </>
                            ) : (
                            <>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('common.unit')}</Form.Label>
                                    <Form.Control
                                        name="unit"
                                        value={formData.unit}
                                        onChange={handleChange}
                                        placeholder={t('common.unit')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={9} className="d-flex align-items-end">
                                <div className="alert alert-light border w-100 mb-3 py-2 px-3">
                                    <strong>{t('materials.compound.computedPrice')}:</strong>{' '}
                                    €{componentsTotal.excl.toFixed(4)} {t('materials.columns.priceExclTax').toLowerCase()} /
                                    €{componentsTotal.incl.toFixed(4)} {t('materials.columns.priceInclTax').toLowerCase()}
                                    <small className="d-block text-muted">{t('materials.compound.computedPriceHint')}</small>
                                </div>
                            </Col>
                            <Col md={12}>
                                <Form.Label>{t('materials.compound.components')} *</Form.Label>
                                <Form.Text className="text-muted d-block mb-2">{t('materials.compound.componentsHint')}</Form.Text>
                                {(formData.components || []).map((row, idx) => {
                                    const selected = materials.find((mm) => String(mm.id) === String(row.component_material_id));
                                    const unitPrices = getUnitPrices(selected);
                                    return (
                                        <div className="d-flex align-items-center gap-2 mb-2" key={idx}>
                                            <Form.Select
                                                value={row.component_material_id}
                                                onChange={(e) => handleComponentRowChange(idx, 'component_material_id', e.target.value)}
                                                style={{ maxWidth: 360 }}
                                                required
                                            >
                                                <option value="">{t('materials.compound.selectMaterial')}</option>
                                                {materials
                                                    .filter((mm) => !editingId || mm.id !== editingId)
                                                    .map((mm) => (
                                                        <option key={mm.id} value={mm.id}>
                                                            {getMaterialName(mm)}{mm.material_type === 'compound' ? ` (${t('materials.compound.badge')})` : ''}
                                                        </option>
                                                    ))}
                                            </Form.Select>
                                            <Form.Control
                                                type="number"
                                                step="0.0001"
                                                min="0.0001"
                                                style={{ maxWidth: 140 }}
                                                value={row.quantity}
                                                onChange={(e) => handleComponentRowChange(idx, 'quantity', e.target.value)}
                                                placeholder={t('materials.compound.componentQuantity')}
                                            />
                                            {selected && (
                                                <small className="text-muted" style={{ minWidth: 120 }}>
                                                    {formatUnitForDisplay(selected.unit)} · €{unitPrices.excl.toFixed(4)}/€{unitPrices.incl.toFixed(4)}
                                                </small>
                                            )}
                                            <Button variant="light" size="sm" className="text-danger" onClick={() => handleRemoveComponentRow(idx)} title={t('materials.compound.removeComponent')}>
                                                <i className="fa-solid fa-trash" />
                                            </Button>
                                        </div>
                                    );
                                })}
                                {(formData.components || []).length === 0 && (
                                    <p className="text-muted small">{t('materials.compound.noComponentsYet')}</p>
                                )}
                                <Button variant="outline-secondary" size="sm" onClick={handleAddComponentRow} className="mb-2">
                                    <i className="fa-solid fa-plus me-1" /> {t('materials.compound.addComponent')}
                                </Button>
                                {componentsTotal.isFoodMixed && (
                                    <div className="alert alert-warning py-2 px-3 small mb-2">
                                        <i className="fa-solid fa-triangle-exclamation me-1" /> {t('materials.compound.mixedFoodWarning')}
                                    </div>
                                )}
                                <p className="text-muted small mb-3">
                                    {componentsTotal.isNonFood ? t('materials.compound.derivedNonFood') : t('materials.compound.derivedFood')}
                                </p>
                            </Col>
                            </>
                            )}
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('materials.vendor')}</Form.Label>
                                    <Form.Control
                                        name="source"
                                        value={formData.source}
                                        onChange={handleChange}
                                        placeholder={t('common.source')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('common.location')}</Form.Label>
                                    <Form.Control
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        placeholder={t('common.location')}
                                    />
                                </Form.Group>
                            </Col>
                            {formData.material_type === 'atomic' && (
                            <>
                            <Col md={12}>
                                <hr className="my-3" />
                                <h6 className="mb-2">{t('materials.tax')}</h6>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="price_includes_tax"
                                        name="price_includes_tax"
                                        label={t('materials.taxInclusive')}
                                        checked={formData.price_includes_tax}
                                        onChange={handleChange}
                                    />
                                    <Form.Text className="text-muted">
                                        {formData.price_includes_tax
                                            ? t('materials.taxInclusiveNote')
                                            : t('materials.taxExclusive')}
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('materials.taxPercent')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        name="tax_percentage"
                                        value={formData.tax_percentage}
                                        onChange={handleChange}
                                        placeholder="e.g. 9"
                                    />
                                    <Form.Text className="text-muted">
                                        {t('materials.taxNote')}
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <hr className="my-3" />
                                <h6 className="mb-2">{t('materials.nonFoodToggle')}</h6>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="is_non_food"
                                        name="is_non_food"
                                        label={t('materials.nonFoodDescription')}
                                        checked={formData.is_non_food}
                                        onChange={handleChange}
                                    />
                                    <Form.Text className="text-muted">
                                        {t('materials.nonFoodNote')}
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            {!formData.is_non_food && (
                                <>
                                    <Col md={12}>
                                        <hr className="my-3" />
                                        <h6 className="mb-2">{t('materials.nutrition')}</h6>
                                        <Form.Text className="text-muted d-block mb-2">
                                            {t('materials.nutritionNote')}
                                        </Form.Text>
                                    </Col>
                                    {/* Required fields */}
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.energy')} *</Form.Label>
                                            <Form.Control type="number" step="0.01" min="0" name="energy_for_purchase" value={formData.energy_for_purchase} onChange={handleChange} placeholder="e.g. 12500" required />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.protein')} *</Form.Label>
                                            <Form.Control type="number" step="0.01" min="0" name="protein_for_purchase" value={formData.protein_for_purchase} onChange={handleChange} placeholder="e.g. 400" required />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.fat')} *</Form.Label>
                                            <Form.Control type="number" step="0.01" min="0" name="fat_for_purchase" value={formData.fat_for_purchase} onChange={handleChange} placeholder="e.g. 100" required />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.carbohydrate')} *</Form.Label>
                                            <Form.Control type="number" step="0.01" min="0" name="carbohydrate_for_purchase" value={formData.carbohydrate_for_purchase} onChange={handleChange} placeholder="e.g. 2500" required />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.fiber')} *</Form.Label>
                                            <Form.Control type="number" step="0.01" min="0" name="fiber_for_purchase" value={formData.fiber_for_purchase} onChange={handleChange} placeholder="e.g. 0" required />
                                            <Form.Text className="text-muted">{t('materials.fiberNote')}</Form.Text>
                                        </Form.Group>
                                    </Col>
                                    {/* Optional sub-fields */}
                                    <Col md={12}>
                                        <p className="text-muted small mb-2">{t('materials.optionalNutrition')}</p>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.saturatedFat')}</Form.Label>
                                            <Form.Control type="number" step="0.01" min="0" name="saturated_fat_for_purchase" value={formData.saturated_fat_for_purchase} onChange={handleChange} placeholder="e.g. 20" />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.transFat')}</Form.Label>
                                            <Form.Control type="number" step="0.01" min="0" name="trans_fat_for_purchase" value={formData.trans_fat_for_purchase} onChange={handleChange} placeholder="e.g. 0" />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.sugars')}</Form.Label>
                                            <Form.Control type="number" step="0.01" min="0" name="sugars_for_purchase" value={formData.sugars_for_purchase} onChange={handleChange} placeholder="e.g. 150" />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.sodium')}</Form.Label>
                                            <Form.Control type="number" step="0.0001" min="0" name="sodium_for_purchase" value={formData.sodium_for_purchase} onChange={handleChange} placeholder="e.g. 0.5" />
                                            <Form.Text className="text-muted">{t('materials.sodiumNote')}</Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <hr className="my-3" />
                                        <h6 className="mb-2">{t('materials.allergens')}</h6>
                                    </Col>
                                    {EU_ALLERGENS.map((allergen) => (
                                        <Col md={3} key={allergen}>
                                            <Form.Group className="mb-2">
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`allergen-${allergen}`}
                                                    label={t(`allergenNames.${allergen}`)}
                                                    checked={(formData.allergens || []).includes(allergen)}
                                                    onChange={() => handleAllergenToggle(allergen)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    ))}
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('materials.mayContain')}</Form.Label>
                                            <Form.Control
                                                name="may_contain"
                                                value={formData.may_contain}
                                                onChange={handleChange}
                                                placeholder={t('materials.mayContainPrefix')}
                                            />
                                        </Form.Group>
                                    </Col>
                                </>
                            )}
                            </>
                            )}
                            <Col md={12}>
                                <Form.Group className="mb-0">
                                    <Form.Label>{t('common.notes')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        placeholder={t('common.additionalNotes')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            {t('common.cancel')}
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? t('common.saving') : (editingId ? t('common.update') : t('materials.addMaterial'))}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default MaterialLogs;
